import React, { useEffect, useState, useCallback, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import { Form, InputGroup } from "react-bootstrap";
import RequiredLabel from "../../CustomCommons/RequiredLabel";
import { BASE_URL } from "../../Const/Url";
import { showToast } from "../../../utils/ToastHelper";
import { logout_func2 } from "../../Const/logoutCommon";
import Loader from "../../CustomCommons/Loader";
import { confirmAndRemoveMedia } from "./mediaRemovalHelper";

const EditHeroBanner = () => {
  const history = useHistory();
  const { slug } = useParams();
  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setOnlogoutHide] = useState(0);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mediaType, setMediaType] = useState(0);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(true);
  const [order, setOrder] = useState("");
  const [imageId, setImageId] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [mobileImageId, setMobileImageId] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [mobileImagePreview, setMobileImagePreview] = useState("");
  const [imageName, setImageName] = useState("");
  const [mobileImageName, setMobileImageName] = useState("");
  const [videoName, setVideoName] = useState("");
  const mobileImageInputRef = useRef(null);

  const uploadDocument = (file, docType, onSuccess) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("document", file);
    formData.append("doc_type", docType);

    axios
      .post(`${BASE_URL}api/v1/auth/documents/upload/`, formData)
      .then((res) => {
        if (res?.data?.status) {
          onSuccess(res?.data?.data?.id);
          showToast("success", "File uploaded successfully");
        } else {
          showToast("error", "Failed to upload file");
        }
      })
      .catch((err) => {
        const message =
          err?.response?.data?.errors?.document?.[0] ||
          err?.response?.data?.message ||
          "Upload failed";
        showToast("error", message);
      });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(file, 0, (id) => {
      setImageId(id);
      setImageName(file.name);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    });
  };

  const handleMobileImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      // Reset input if no file selected
      if (mobileImageInputRef.current) {
        mobileImageInputRef.current.value = "";
      }
      return;
    }
    uploadDocument(file, 0, (id) => {
      setMobileImageId(id);
      setMobileImageName(file.name);
      const reader = new FileReader();
      reader.onload = () => setMobileImagePreview(reader.result);
      reader.readAsDataURL(file);
    });
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(file, 1, (id) => {
      setVideoId(id);
      setVideoName(file.name);
    });
  };

  const resetMedia = () => {
    setImageId(null);
    setImagePreview("");
    setImageName("");
    setMobileImageId(null);
    setMobileImagePreview("");
    setMobileImageName("");
    setVideoId(null);
    setVideoName("");
  };

  const handleRemoveMedia = (type) => {
    confirmAndRemoveMedia({
      onConfirm: () => {
        if (type === "image") {
          setImageId(null);
          setImagePreview("");
          setImageName("");
        } else if (type === "mobileImage") {
          setMobileImageId(null);
          setMobileImagePreview("");
          setMobileImageName("");
          if (mobileImageInputRef.current) {
            mobileImageInputRef.current.value = "";
          }
        } else if (type === "video") {
          setVideoId(null);
          setVideoName("");
        }
      },
    });
  };

  const fetchBannerDetails = useCallback(() => {
    if (!slug) {
      showToast("error", "Missing hero banner identifier.");
      history.push("/website-settings/hero-banner-list");
      return;
    }

    setIsFetching(true);
    axios
      .get(`${BASE_URL}api/v1/frontend/admin/v2/hero-banners/${slug}/`)
      .then((res) => {
        const data = res?.data?.data;
        if (!data) {
          showToast("error", "Failed to fetch hero banner details.");
          history.push("/website-settings/hero-banner-list");
          return;
        }
        setName(data?.name || "");
        setDescription(data?.description || "");
        setMediaType(data?.media_type ?? 0);
        setStartAt(data?.start_at ? data.start_at.slice(0, 16) : "");
        setEndAt(data?.end_at ? data.end_at.slice(0, 16) : "");
        setIsActive(data?.is_active ?? true);
        setIsDefault(data?.is_default ?? true);
        setOrder(data?.order ?? "");
        setImageId(data?.image || null);
        setVideoId(data?.video || null);
        setMobileImageId(data?.mobile_banner || null);
        if (data?.image_details?.doc_url) {
          setImagePreview(data.image_details.doc_url);
          setImageName(data.image_details.document || "");
        }
        if (data?.mobile_banner_details?.doc_url) {
          setMobileImagePreview(data.mobile_banner_details.doc_url);
          setMobileImageName(data.mobile_banner_details.document || "");
        }
        if (data?.video_details?.document) {
          setVideoName(data.video_details.document);
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          logout_func2(setOnlogoutHide, history);
          return;
        }
        showToast("error", "Unable to load hero banner data.");
        history.push("/website-settings/hero-banner-list");
      })
      .finally(() => setIsFetching(false));
  }, [slug, history]);

  useEffect(() => {
    fetchBannerDetails();
  }, [fetchBannerDetails]);

  const validateForm = () => {
    if (!name.trim()) {
      showToast("error", "Name is required.");
      return false;
    }
    if (!order && order !== 0) {
      showToast("error", "Order is required.");
      return false;
    }
    // if (!startAt || !endAt) {
    //   showToast("error", "Start and end time are required.");
    //   return false;
    // }
    if (mediaType === 0 && !imageId) {
      showToast("error", "Please upload a desktop image.");
      return false;
    }
    if (mediaType === 0 && !mobileImageId) {
      showToast("error", "Please upload a mobile image.");
      return false;
    }
    if (mediaType === 1 && !videoId) {
      showToast("error", "Please upload a video.");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      main_category: 0,
      name: name.trim(),
      media_type: Number(mediaType),
      description: description.trim(),
      start_at: startAt ? new Date(startAt).toISOString() : null,
      end_at: endAt ? new Date(endAt).toISOString() : null,
      is_active: isActive,
      is_default: isDefault,
      order: Number(order),
      image: mediaType === 0 ? imageId : null,
      mobile_banner: mediaType === 0 ? mobileImageId : null,
      video: mediaType === 1 ? videoId : null,
    };

    setIsSubmitting(true);
    axios
      .patch(
        `${BASE_URL}api/v1/frontend/admin/v2/hero-banners/${slug}/`,
        payload
      )
      .then(() => {
        showToast("success", "Hero banner updated successfully.");
        history.push("/website-settings/hero-banner-list");
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          logout_func2(setOnlogoutHide, history);
          return;
        }
        const message =
          err?.response?.data?.errors?.non_field_errors?.[0] ||
          err?.response?.data?.message ||
          "Failed to update hero banner. Please try again.";
        showToast("error", message);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleCancel = () => {
    history.push("/website-settings/hero-banner-list");
  };

  if (isFetching) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent new-categorey-parent an">
      <div className="bg-white add-div">
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ padding: "20px 35px", border: "0.5px solid #E0E0E0" }}
        >
          <h3 style={{ fontSize: "23px", marginTop: "6px" }}>
            Edit Hero Banner
          </h3>
        </div>

        <div
          style={{
            padding: "20px 35px",
            borderBottom: "0.5px solid #E0E0E0",
            borderRight: "0.5px solid #E0E0E0",
            borderLeft: "0.5px solid #E0E0E0",
          }}
          className="row"
        >
          <div className="col-12 col-md-6">
            <RequiredLabel text="Name" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="Name"
                style={{ backgroundColor: "#FAFAFA" }}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Order" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="Order"
                style={{ backgroundColor: "#FAFAFA" }}
                value={order}
                type="number"
                min={0}
                onChange={(e) => setOrder(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12">
            <RequiredLabel text="Description" />
            <InputGroup className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                style={{ backgroundColor: "#FAFAFA" }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            {/* <RequiredLabel text="Start At" /> */}
            <p>Start At</p>
            <InputGroup className="mb-3">
              <Form.Control
                type="datetime-local"
                style={{ backgroundColor: "#FAFAFA" }}
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            {/* <RequiredLabel text="End At" /> */}
            <p>End At</p>
            <InputGroup className="mb-3">
              <Form.Control
                type="datetime-local"
                style={{ backgroundColor: "#FAFAFA" }}
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Media Type" />
            <div className="mb-3" style={{ height: "47px" }}>
              <Form.Select
                size="lg"
                style={{
                  backgroundColor: "#FAFAFA",
                  fontSize: "16px",
                  height: "47px",
                  borderRadius: "8px",
                  color: "#828282",
                }}
                value={mediaType}
                onChange={(e) => {
                  setMediaType(Number(e.target.value));
                  resetMedia();
                }}
              >
                <option value={0}>Image</option>
                <option value={1}>Video</option>
              </Form.Select>
            </div>
          </div>

          {mediaType === 0 ? (
            <>
              <div className="col-12 col-md-6">
                <RequiredLabel text="Upload Image (Desktop)" />
                <p style={{ marginTop: "10px", color: "red" }}>
                  NB: Upload image in (height-820px, width-1920px) for Banner.
                </p>
                <div
                  className="w-100 browse-main d-flex align-items-center"
                  style={{
                    height: "55px",
                    border: "1px solid #E0E0E0",
                    borderRadius: "7px",
                    overflowX: "hidden",
                  }}
                >
                  <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                    {imageName ? "File Chosen" : "No File Chosen"}
                  </p>
                  <div className="file-up">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                    <p>Browse</p>
                  </div>
                </div>
                {imagePreview && (
                  <div className="my-2">
                    <img
                      height={90}
                      width={90}
                      src={imagePreview}
                      alt="preview"
                    />
                    <br />
                    <button
                      onClick={() => handleRemoveMedia("image")}
                      className="remove-btn btn btn-small mt-1 rounded"
                      style={{ border: "1px solid gray !important" }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <div className="col-12 col-md-6">
                <RequiredLabel text="Upload Image (Mobile)" />
                <p style={{ marginTop: "10px", color: "red" }}>
                  {" "}
                  NB: Upload image in (height-450px, width-360px) for Banner.{" "}
                </p>
                <div
                  className="w-100 browse-main d-flex align-items-center"
                  style={{
                    height: "55px",
                    border: "1px solid #E0E0E0",
                    borderRadius: "7px",
                    overflowX: "hidden",
                  }}
                >
                  <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                    {mobileImageName ? "File Chosen" : "No File Chosen"}
                  </p>
                  <div className="file-up">
                    <input
                      ref={mobileImageInputRef}
                      key={`mobile-image-${mobileImageId || "new"}`}
                      type="file"
                      accept="image/*"
                      onChange={handleMobileImageUpload}
                    />
                    <p>Browse</p>
                  </div>
                </div>
                {mobileImagePreview && (
                  <div className="my-2">
                    <img
                      height={90}
                      width={90}
                      src={mobileImagePreview}
                      alt="mobile preview"
                    />
                    <br />
                    <button
                      onClick={() => handleRemoveMedia("mobileImage")}
                      className="remove-btn btn btn-small mt-1 rounded"
                      style={{ border: "1px solid gray !important" }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="col-12 col-md-6">
              <RequiredLabel text="Upload Video" />
              <div
                className="w-100 browse-main d-flex alignments-center"
                style={{
                  height: "55px",
                  border: "1px solid #E0E0E0",
                  borderRadius: "7px",
                  overflowX: "hidden",
                }}
              >
                <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                  {videoName ? "File Chosen" : "No File Chosen"}
                </p>
                <div className="file-up">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                  />
                  <p>Browse</p>
                </div>
              </div>
              {videoName && (
                <div className="my-2">
                  <button
                    onClick={() => handleRemoveMedia("video")}
                    className="remove-btn btn btn-small mt-1 rounded"
                    style={{ border: "1px solid gray !important" }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="col-12 mt-4">
            <div className="row">
              <div className="col-12 col-md-6">
                <h5>Status</h5>
                <p style={{ color: "#8E8E93" }}>( Toggle banner visibility )</p>
                <div
                  className="d-flex justify-content-between align-items-center py-3 px-3"
                  style={{ backgroundColor: "#F2F2F7", borderRadius: "8px" }}
                >
                  <span>{isActive ? "Active" : "Inactive"}</span>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <h5>Default Banner</h5>
                <p style={{ color: "#8E8E93" }}>( Set banner as default )</p>
                <div
                  className="d-flex justify-content-between align-items-center py-3 px-3"
                  style={{ backgroundColor: "#F2F2F7", borderRadius: "8px" }}
                >
                  <span>{isDefault ? "Yes" : "No"}</span>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleSubmit}
              className="save-btn me-2 mt-2"
              style={{ background: "#000", color: "white" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancel}
              className="save-btn mt-2"
              style={{ border: ".4px solid #E0E0E0 !important" }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditHeroBanner;
